/* Product buy box: gallery (crossfade desktop / scroll-snap swipe mobile),
   pack-selector state (no reload, no Section Rendering fetch), add to cart,
   sticky CTA bar.

   Cart integration: dispatches the theme's real CartLinesUpdateEvent from
   '@shopify/events' (same contract assets/product-form.js uses) so the
   existing header cart bubble (cart-icon.js) and cart drawer
   (cart-drawer.js, when settings.auto_open_cart_drawer is on) update
   themselves for free. The import is dynamic and wrapped in try/catch: if
   the theme's import map isn't present (e.g. this file loaded in an
   isolated preview harness with no '@shopify/events' entry), we fall back
   to updating any [data-up-product-cart-count] / .cart-bubble elements
   directly, and to opening #cart-drawer by calling its public open()
   method, or navigating to /cart if no drawer exists. */

(function () {
  'use strict';

  function isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  /* ------------------------------------------------------------------
     Gallery
     ------------------------------------------------------------------ */

  function initGallery(section) {
    var gallery = section.querySelector('[data-up-product-gallery]');
    if (!gallery) return;

    var main = gallery.querySelector('[data-up-product-main]');
    var images = Array.prototype.slice.call(gallery.querySelectorAll('[data-up-product-main-img]'));
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-up-product-thumb]'));
    var counterCurrent = gallery.querySelector('[data-up-product-counter-current]');

    if (images.length === 0) return;

    var scrolling = false;

    function setActive(index, options) {
      options = options || {};
      index = Math.max(0, Math.min(images.length - 1, index));

      images.forEach(function (img, i) {
        img.classList.toggle('is-active', i === index);
      });
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });
      if (counterCurrent) counterCurrent.textContent = String(index + 1);

      if (options.scrollMain && isMobile() && main) {
        scrolling = true;
        main.scrollTo({ left: main.clientWidth * index, behavior: 'smooth' });
        window.setTimeout(function () {
          scrolling = false;
        }, 400);
      }
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = parseInt(thumb.getAttribute('data-index'), 10) || 0;
        setActive(index, { scrollMain: true });
      });
    });

    if (main) {
      var scrollTimeout;
      main.addEventListener(
        'scroll',
        function () {
          if (!isMobile() || scrolling) return;
          window.clearTimeout(scrollTimeout);
          scrollTimeout = window.setTimeout(function () {
            var width = main.clientWidth || 1;
            var index = Math.round(main.scrollLeft / width);
            setActive(index, { scrollMain: false });
          }, 80);
        },
        { passive: true }
      );
    }

    setActive(0);
  }

  /* ------------------------------------------------------------------
     UGC media strip: flex + native scroll-snap + scrollBy(), same
     mechanism as every other carousel on this site (see up-bestsellers.js).
     Reference only shows its prev/next arrows when the track actually
     overflows (desktop fits exactly 3 items and hides them entirely);
     mirror that instead of always showing disabled-looking buttons.
     ------------------------------------------------------------------ */

  function initUgcCarousel(section) {
    var track = section.querySelector('[data-up-product-ugc-track]');
    if (!track || track.hasAttribute('data-up-product-ugc-ready')) return;
    track.setAttribute('data-up-product-ugc-ready', 'true');

    var controls = section.querySelector('[data-up-product-ugc-controls]');
    var prevBtn = section.querySelector('[data-up-product-ugc-prev]');
    var nextBtn = section.querySelector('[data-up-product-ugc-next]');

    function step() {
      var item = track.querySelector('.up-product__ugc-item');
      if (!item) return track.clientWidth;
      var trackStyle = getComputedStyle(track);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    function update() {
      var overflows = track.scrollWidth - track.clientWidth > 1;
      if (controls) controls.hidden = !overflows;
      if (!overflows) return;

      var maxScroll = track.scrollWidth - track.clientWidth;
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft >= maxScroll - 1;
      if (prevBtn) prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      if (nextBtn) nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    }

    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------------------
     Detail rows accordion (product details / ingredients / shipping).
     JS-measures each panel's natural height and animates the `height`
     property 250ms ease (--up-product-detail-t in up-product.css).
     Rows open and close independently -- no exclusive-open, unlike the
     PDP's lower FAQ section, which uses an instant reveal and closes
     any other open row. Do not merge the two behaviours.
     ------------------------------------------------------------------ */

  function initDetailAccordion(section) {
    var wrap = section.querySelector('[data-up-product-accordion]');
    if (!wrap || wrap.hasAttribute('data-up-product-accordion-ready')) return;
    wrap.setAttribute('data-up-product-accordion-ready', 'true');

    var rows = Array.prototype.slice.call(wrap.querySelectorAll('[data-up-product-detail-row]'));

    rows.forEach(function (row) {
      var trigger = row.querySelector('[data-up-product-detail-trigger]');
      var panel = row.querySelector('[data-up-product-detail-panel]');
      var content = row.querySelector('[data-up-product-detail-content]');
      if (!trigger || !panel || !content) return;

      function openRow() {
        row.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        var target = content.getBoundingClientRect().height;
        panel.style.height = target + 'px';
      }

      function closeRow() {
        // Start from an explicit pixel height (not "auto") so the browser
        // has something to transition away from.
        panel.style.height = panel.getBoundingClientRect().height + 'px';
        // Force a reflow so the browser registers the starting height
        // before the next frame flips it to 0 -- otherwise the two
        // writes collapse into one and there's nothing to animate.
        void panel.offsetHeight;
        window.requestAnimationFrame(function () {
          panel.style.height = '0px';
        });
        row.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }

      panel.addEventListener('transitionend', function (event) {
        if (event.propertyName !== 'height') return;
        if (row.classList.contains('is-open')) {
          // Let the panel breathe if content reflows later (viewport
          // resize, font load) instead of staying pinned to a stale px.
          panel.style.height = 'auto';
        }
      });

      trigger.addEventListener('click', function () {
        if (row.classList.contains('is-open')) {
          closeRow();
        } else {
          openRow();
        }
      });
    });

    window.addEventListener('resize', function () {
      rows.forEach(function (row) {
        if (!row.classList.contains('is-open')) return;
        var panel = row.querySelector('[data-up-product-detail-panel]');
        var content = row.querySelector('[data-up-product-detail-content]');
        if (!panel || !content) return;
        // Only re-measure open rows already resting at 'auto'; a row mid
        // animation will settle itself via its own transitionend.
        if (panel.style.height === 'auto') {
          panel.style.height = content.getBoundingClientRect().height + 'px';
          panel.style.height = 'auto';
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Sticky CTA bar
     ------------------------------------------------------------------ */

  /* Horizon does not scroll the window. Its .page-wrapper is the scroll
     container (overflow: auto) while html and body are overflow: hidden and
     pinned to viewport height, so window.scrollY reads 0 no matter how far
     down the page the visitor is. Read the offset off whichever element
     actually moves, and bind to both so this keeps working if the wrapper
     ever goes away. */
  function scrollHost() {
    var wrap = document.querySelector('.page-wrapper');
    return wrap && wrap.scrollHeight > wrap.clientHeight + 1 ? wrap : null;
  }

  function scrollOffset() {
    var host = scrollHost();
    return host ? host.scrollTop : window.scrollY;
  }

  function initStickyBar(section) {
    var bar = section.querySelector('[data-up-product-sticky]');
    if (!bar) return;

    function threshold() {
      return isMobile() ? 1500 : 1100;
    }

    function onScroll() {
      var visible = scrollOffset() > threshold();
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    var host = scrollHost();
    if (host) host.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
  }

  /* ------------------------------------------------------------------
     Cart: header count fallback + drawer/navigate
     ------------------------------------------------------------------ */

  function updateHeaderCartCountFallback(itemCount) {
    document.querySelectorAll('[data-up-cart-count], .cart-bubble__text-count, [data-up-product-cart-count]').forEach(function (el) {
      el.textContent = itemCount < 100 ? String(itemCount) : '99+';
      el.classList.toggle('hidden', itemCount === 0);
    });
    document.querySelectorAll('.cart-bubble').forEach(function (el) {
      el.classList.toggle('visually-hidden', itemCount === 0);
    });
  }

  function openCartDrawerOrNavigate(cartUrl) {
    var drawer = document.querySelector('#cart-drawer');

    if (!drawer) {
      window.location.href = cartUrl;
      return;
    }

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    /* Custom element present but not upgraded yet -- give it a moment,
       then fall back to navigation so the click never dead-ends. */
    var upgraded =
      window.customElements && typeof window.customElements.whenDefined === 'function'
        ? window.customElements.whenDefined('theme-drawer').catch(function () {})
        : Promise.resolve();
    var timeout = new Promise(function (resolve) {
      window.setTimeout(resolve, 500);
    });

    Promise.race([upgraded, timeout]).then(function () {
      if (typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = cartUrl;
      }
    });
  }

  async function addToCart(packSelector, variantId, quantity) {
    var cartAddUrl = packSelector.getAttribute('data-cart-add-url') || '/cart/add.js';
    var cartUrl = packSelector.getAttribute('data-cart-url') || '/cart';
    var cartJsonUrl = cartUrl.replace(/\/$/, '') + '.js';

    var CartLinesUpdateEvent = null;
    try {
      var mod = await import('@shopify/events');
      CartLinesUpdateEvent = mod.CartLinesUpdateEvent;
    } catch (e) {
      CartLinesUpdateEvent = null;
    }

    var deferred = null;
    if (CartLinesUpdateEvent) {
      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: quantity }],
          promise: deferred.promise,
        })
      );
    }

    try {
      var response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity }),
      });
      var data = await response.json();

      if (!response.ok || data.status) {
        throw new Error((data && (data.description || data.message)) || 'Could not add this item to your cart.');
      }

      var cartResponse = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
      var cartJson = await cartResponse.json();

      if (deferred && CartLinesUpdateEvent) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
          detail: {
            items: cartJson.items,
            itemCount: quantity,
            source: 'up-product',
            didError: false,
          },
        });
      } else {
        updateHeaderCartCountFallback(cartJson.item_count);
      }

      openCartDrawerOrNavigate(cartUrl);
      return { ok: true };
    } catch (error) {
      if (deferred) deferred.reject(error);
      return { ok: false, error: error };
    }
  }

  /* ------------------------------------------------------------------
     Pack selector
     ------------------------------------------------------------------ */

  function initPacks(section) {
    var packSelector = section.querySelector('[data-up-product-packs]');
    if (!packSelector) return;

    var radios = Array.prototype.slice.call(packSelector.querySelectorAll('[data-up-product-pack-radio]'));
    var labels = Array.prototype.slice.call(packSelector.querySelectorAll('.up-product__pack'));
    var addButton = section.querySelector('[data-up-product-add]');
    var addLabel = section.querySelector('[data-up-product-add-label]');
    var errorEl = section.querySelector('[data-up-product-error]');
    var stickyPrice = section.querySelector('[data-up-product-sticky-price]');
    var stickyAdd = section.querySelector('[data-up-product-sticky-add]');
    var ctaLabel = (addButton && addButton.getAttribute('data-cta-label')) || 'Add to Cart';

    function updateSelection(radio) {
      var price = radio.getAttribute('data-price');
      var available = radio.getAttribute('data-available') === 'true';
      var variantId = radio.value;

      labels.forEach(function (label) {
        label.classList.toggle('is-selected', label.querySelector('[data-up-product-pack-radio]') === radio);
      });

      if (addButton) {
        addButton.setAttribute('data-variant-id', variantId);
        addButton.disabled = !available;
      }
      if (addLabel) {
        addLabel.textContent = available ? ctaLabel + ' - ' + price : 'Sold Out';
      }
      if (stickyAdd) {
        stickyAdd.setAttribute('data-variant-id', variantId);
        stickyAdd.disabled = !available;
      }
      if (stickyPrice) {
        stickyPrice.textContent = price;
      }
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) updateSelection(radio);
      });
    });

    var initialSelected = radios.filter(function (r) {
      return r.checked;
    })[0];
    if (initialSelected) updateSelection(initialSelected);

    async function handleAdd(button) {
      if (!button || button.disabled) return;
      var variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      var result = await addToCart(packSelector, variantId, 1);

      button.removeAttribute('aria-busy');
      button.disabled = false;

      if (!result.ok) {
        if (errorEl) {
          errorEl.textContent = (result.error && result.error.message) || 'Could not add this item to your cart.';
          errorEl.hidden = false;
        }
      }
    }

    if (addButton) {
      addButton.addEventListener('click', function () {
        handleAdd(addButton);
      });
    }
    if (stickyAdd) {
      stickyAdd.addEventListener('click', function () {
        handleAdd(stickyAdd);
      });
    }
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function initSection(section) {
    if (section.hasAttribute('data-up-product-ready')) return;
    section.setAttribute('data-up-product-ready', 'true');

    initGallery(section);
    initPacks(section);
    initStickyBar(section);
    initUgcCarousel(section);
    initDetailAccordion(section);
  }

  function initAll() {
    document.querySelectorAll('.up-product').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target && event.target.querySelector ? event.target.querySelector('.up-product') : null;
    if (section) initSection(section);
  });
})();

/* UGC prehravani. Tlacitko drive jen leželo pres fotku a nic nedelalo.
   Klik prepne polozku do is-playing, spusti video a po dohrani se vrati
   zpatky na fotku. Vzdy bezi jen jedno video. */
(function () {
  function stopAll(except) {
    document.querySelectorAll('.up-product__ugc-item-wrap.is-playing').forEach(function (w) {
      if (w === except) return;
      var v = w.querySelector('.up-product__ugc-video');
      if (v) { v.pause(); v.currentTime = 0; }
      w.classList.remove('is-playing');
    });
  }

  document.addEventListener('click', function (e) {
    /* Klik bere cela dlazdice, ne jen kruh: pri 40px by byl kruh maly cil. */
    var wrap = e.target.closest('.up-product__ugc-item-wrap');
    if (!wrap || !wrap.querySelector('[data-up-ugc-play]')) return;
    if (wrap.classList.contains('is-playing')) return;
    var video = wrap && wrap.querySelector('.up-product__ugc-video');
    if (!video) return;
    stopAll(wrap);
    wrap.classList.add('is-playing');
    video.muted = false;
    video.currentTime = 0;
    var p = video.play();
    if (p && p.catch) {
      p.catch(function () { video.muted = true; video.play(); });
    }
    video.onended = function () { wrap.classList.remove('is-playing'); };
  });
})();
